import { useState, useEffect, useRef } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

const ROLES = ['super_admin', 'admin', 'dispatcher', 'sales', 'driver', 'maintenance'];
const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dispatcher: 'Dispatcher', sales: 'Sales', driver: 'Driver', maintenance: 'Maintenance' };
const ROLE_COLORS = { super_admin: '#ef4444', admin: '#f59e0b', dispatcher: '#3b82f6', sales: '#8b5cf6', driver: '#10b981', maintenance: '#f97316' };

const TIER_OPTIONS = [
  { value: '', label: 'Not Set (uses role)' },
  { value: 'user', label: 'User' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];
const TIER_COLORS = { user: '#10b981', manager: '#3b82f6', admin: '#f59e0b', super_admin: '#ef4444' };

function ini(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function Avatar({ user, size = 36 }) {
  if (user.photo_url) {
    return (
      <img
        src={user.photo_url}
        alt={user.full_name}
        style={{
          width: size, height: size, borderRadius: '50%', objectFit: 'cover',
          border: '2px solid #e2e8f0', flexShrink: 0,
        }}
      />
    );
  }
  const rc = ROLE_COLORS[user.role] || '#94a3b8';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `${rc}18`, border: `2px solid ${rc}40`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: rc,
    }}>
      {ini(user.full_name)}
    </div>
  );
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [error, setError] = useState('');

  async function load() {
    try { setUsers(await api.getUsers()); }
    catch (err) { setError(err.message); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleDeactivate(id) {
    if (!window.confirm('Deactivate this user? They will not be able to log in.')) return;
    try { await api.deactivateUser(id); load(); }
    catch (err) { alert(err.message); }
  }

  async function handleReactivate(id) {
    try { await api.reactivateUser(id); load(); }
    catch (err) { alert(err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('PERMANENTLY DELETE this user? This cannot be undone!')) return;
    try { await api.deleteUser(id); load(); }
    catch (err) { alert(err.message); }
  }

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>User Management</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New User</button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th></th><th>Username</th><th>Display Name</th><th>Role</th><th>Tier</th><th>Position</th><th>Phone</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={u.is_active ? '' : 'row-inactive'}>
                <td style={{ width: 48, padding: '8px 4px 8px 12px' }}>
                  <Avatar user={u} size={36} />
                </td>
                <td>{u.username}</td>
                <td>{u.full_name}</td>
                <td><span className="role-badge" style={{ background: ROLE_COLORS[u.role] }}>{ROLE_LABELS[u.role]}</span></td>
                <td>{u.permission_tier ? (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: `${TIER_COLORS[u.permission_tier]}18`, color: TIER_COLORS[u.permission_tier], textTransform: 'capitalize' }}>
                    {u.permission_tier === 'super_admin' ? 'Super Admin' : u.permission_tier}
                  </span>
                ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>\u2014</span>}</td>
                <td>{u.position_name ? (
                  <span style={{ fontSize: 12, color: '#374151' }}>{u.position_name}{u.team_name ? <span style={{ color: '#9ca3af' }}> · {u.team_name}</span> : null}</span>
                ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>\u2014</span>}</td>
                <td>{u.phone || '\u2014'}</td>
                <td>
                  <span className={`status-pill ${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                  {u.is_active && u.show_on_leaderboard === 0 ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>Hidden from leaderboards</div> : null}
                </td>
                <td>
                  <button className="btn btn-sm btn-muted" onClick={() => setEditUser(u)}>Edit</button>
                  {u.permission_tier !== 'super_admin' && u.is_active ? (
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeactivate(u.id)}>Deactivate</button>
                  ) : null}
                  {u.permission_tier !== 'super_admin' && !u.is_active ? (
                    <>
                      <button className="btn btn-sm btn-success" onClick={() => handleReactivate(u.id)}>Reactivate</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(u.id)}>Delete</button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <UserFormModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editUser && (
        <UserFormModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
    </div>
  );
}

function UserFormModal({ user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    username: user?.username || '',
    password: '',
    full_name: user?.full_name || '',
    role: user?.role || 'dispatcher',
    permission_tier: user?.permission_tier || '',
    company_position_id: user?.company_position_id || '',
    manager_user_id: user?.manager_user_id || '',
    phone: user?.phone || '',
    is_active: user ? user.is_active : 1,
    show_on_leaderboard: user ? (user.show_on_leaderboard ?? 1) : 1,
  });
  const [positions, setPositions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(user?.photo_url || null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.getPositions().then(setPositions).catch(() => {});
    api.getActiveUsers().then(setAllUsers).catch(() => {});
  }, []);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const result = await api.uploadUserPhoto(user.id, formData);
      setPhotoUrl(result.photo_url);
    } catch (err) {
      setError(err.message || 'Failed to upload photo');
    }
    setPhotoUploading(false);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handlePhotoRemove() {
    setPhotoUploading(true);
    setError('');
    try {
      await api.deleteUserPhoto(user.id);
      setPhotoUrl(null);
    } catch (err) {
      setError(err.message || 'Failed to remove photo');
    }
    setPhotoUploading(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      // Normalize empty-string selects to null for nullable DB columns
      if (payload.manager_user_id === '') payload.manager_user_id = null;
      if (payload.company_position_id === '') payload.company_position_id = null;
      if (isEdit) await api.updateUser(user.id, payload);
      else await api.createUser(payload);
      onSaved();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  }

  const rc = ROLE_COLORS[form.role] || '#94a3b8';

  return (
    <Modal title={isEdit ? `Edit User: ${user.username}` : 'Create User'} onClose={onClose}>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>

        {/* Photo upload — only for existing users */}
        {isEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20, padding: '16px 20px', background: '#f8fafc', borderRadius: 12 }}>
            {/* Current photo / placeholder */}
            <div style={{ position: 'relative' }}>
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={user.full_name}
                  style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #e2e8f0' }}
                />
              ) : (
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: `${rc}18`, border: `3px solid ${rc}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 28, fontWeight: 800, color: rc,
                }}>
                  {ini(form.full_name || user.full_name)}
                </div>
              )}
              {photoUploading && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{ width: 20, height: 20, border: '3px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Profile Photo</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                    background: '#fff', fontSize: 12, fontWeight: 600, color: '#475569',
                    cursor: 'pointer',
                  }}
                >
                  {photoUrl ? 'Change Photo' : 'Upload Photo'}
                </button>
                {photoUrl && (
                  <button
                    type="button"
                    onClick={handlePhotoRemove}
                    disabled={photoUploading}
                    style={{
                      padding: '6px 14px', borderRadius: 8, border: '1px solid #fecaca',
                      background: '#fff', fontSize: 12, fontWeight: 600, color: '#ef4444',
                      cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>JPG, PNG, or WebP. Max 5MB. Cropped to square.</div>
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Username</label>
            <input value={form.username} onChange={e => set('username', e.target.value)} required disabled={isEdit} />
          </div>
          <div className="form-group">
            <label>{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)} required={!isEdit} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Display Name</label>
            <input value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Role <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>(legacy)</span></label>
            <select value={form.role} onChange={e => set('role', e.target.value)}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Permission Tier</label>
            <select value={form.permission_tier} onChange={e => set('permission_tier', e.target.value)}>
              {TIER_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Company Position</label>
            <select value={form.company_position_id} onChange={e => set('company_position_id', e.target.value ? Number(e.target.value) : '')}>
              <option value="">No position assigned</option>
              {(() => {
                const grouped = {};
                positions.forEach(p => {
                  if (!grouped[p.team_name]) grouped[p.team_name] = [];
                  grouped[p.team_name].push(p);
                });
                return Object.entries(grouped).map(([teamName, teamPositions]) => (
                  <optgroup key={teamName} label={teamName}>
                    {teamPositions.map(p => (
                      <option key={p.id} value={p.id}>{p.name}{p.is_supervisor ? ' ★' : ''}</option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Reports To</label>
            <select value={form.manager_user_id} onChange={e => set('manager_user_id', e.target.value ? Number(e.target.value) : '')}>
              <option value="">No manager</option>
              {allUsers
                .filter(u => !user || u.id !== user.id) // can't report to yourself
                .map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}{u.position_name ? ` — ${u.position_name}` : ''}
                  </option>
                ))}
            </select>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              This user's direct manager. Automatically added as supervisor on workflow items they're assigned to.
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Phone (for SMS)</label>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+15551234567" />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={form.is_active} onChange={e => set('is_active', Number(e.target.value))}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Leaderboards</label>
            <select value={form.show_on_leaderboard} onChange={e => set('show_on_leaderboard', Number(e.target.value))}>
              <option value={1}>Show on leaderboards</option>
              <option value={0}>Hide from leaderboards</option>
            </select>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              Hidden users stay able to log in but don't appear on any leaderboard. Inactive users are always hidden.
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-muted" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create User')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
